import React, { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Avatar, Dropdown, Select, message } from 'antd';
import {
  DashboardOutlined, ShoppingOutlined, AppstoreOutlined,
  MessageOutlined, InfoCircleOutlined, FileTextOutlined,
  LogoutOutlined, UserOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { authApi } from '../services/api';
import i18n from '../i18n';
import { useTranslation } from 'react-i18next';

const { Sider, Content, Header } = Layout;

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'zh', label: '中文' },
];

export default function AdminLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [lang, setLang] = useState(i18n.language || 'en');

  useEffect(() => {
    i18n.changeLanguage(lang);
    localStorage.setItem('admin_lang', lang);
  }, [lang]);

  useEffect(() => {
    const savedLang = localStorage.getItem('admin_lang');
    if (savedLang) setLang(savedLang);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    authApi.me().then((res) => setUser(res.data)).catch(() => {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    });
  }, [navigate]);

  const handleLogout = async () => {
    try { await authApi.logout(); } catch (e) { /* ignore */ }
    localStorage.removeItem('admin_token');
    message.success(t('admin.logout'));
    navigate('/admin/login');
  };

  const menuItems = [
    { key: '/admin', icon: <DashboardOutlined />, label: <Link to="/admin">{t('admin.dashboard')}</Link> },
    { key: '/admin/products', icon: <ShoppingOutlined />, label: <Link to="/admin/products">{t('admin.products')}</Link> },
    { key: '/admin/categories', icon: <AppstoreOutlined />, label: <Link to="/admin/categories">{t('admin.categories')}</Link> },
    { key: '/admin/inquiries', icon: <MessageOutlined />, label: <Link to="/admin/inquiries">{t('admin.inquiries')}</Link> },
    { key: '/admin/company-info', icon: <InfoCircleOutlined />, label: <Link to="/admin/company-info">{t('admin.companyInfo')}</Link> },
    { key: '/admin/pages', icon: <FileTextOutlined />, label: <Link to="/admin/pages">{t('admin.pages')}</Link> },
    { key: '/admin/certifications', icon: <SafetyCertificateOutlined />, label: <Link to="/admin/certifications">{t('admin.certifications')}</Link> },
  ];

  const userMenu = {
    items: [
      { key: 'logout', icon: <LogoutOutlined />, label: t('admin.logout'), onClick: handleLogout },
    ],
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} style={{ background: '#1a2332' }} theme="dark">
        <div style={{ padding: '20px 16px', textAlign: 'center' }}>
          <img src="/images/logo.png" alt="Huanyu" style={{ width: collapsed ? 60 : 220, objectFit: 'contain' }} />
        </div>
        <Menu theme="dark" mode="inline" selectedKeys={[location.pathname]} items={menuItems} style={{ background: '#1a2332' }} />
      </Sider>
      <Layout>
        <Header style={{
          background: '#fff', padding: '0 24px', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>{t('admin.panelTitle')}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Select value={lang} onChange={setLang} style={{ width: 120 }} options={LANGUAGES} size="small" />
            <Dropdown menu={userMenu}>
              <Button type="text" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar size="small" icon={<UserOutlined />} />
                <span>{user?.name || 'Admin'}</span>
              </Button>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: 24, padding: 24, background: '#fff', borderRadius: 8, minHeight: 280 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
